import React, { Component } from 'react';
import config from 'react-global-configuration';
import $ from 'jquery';
import { isApiResponse } from '../helpers/utils.js';
import MultiCsvReportDownload from '../components/DownloadReport/MultiCsvReportDownload.jsx';

function formatLabel(label) {
  return label
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

export function ocFormatRequest(dtParams, filters) {
  return JSON.stringify({
    rows_per_page: dtParams.length || 1,
    page_number: dtParams.start !== undefined ? Math.floor(dtParams.start / (dtParams.length || 1)) + 1 : 1,
    search_query: dtParams.search ? (typeof dtParams.search === 'object' ? dtParams.search.value : dtParams.search) : '',
    order: dtParams.order && dtParams.order[0] ? String(dtParams.order[0].column) : '-1',
    order_type: dtParams.order && dtParams.order[0] ? dtParams.order[0].dir : 'desc',
    draw: dtParams.draw || 1,
    filters: filters
  });
}

/**
 * Reusable ticket detail table component.
 *
 * Props:
 *   tableId        {string}   - Unique HTML id for the DataTable element
 *   apiUrl         {string}   - API path appended to config API_URL
 *   postData       {object}   - POST body / filters sent to the details API
 *   title          {string}   - Heading text
 *   columnType     {string}   - Column type label shown in heading
 *   objectType     {string}   - Renders Innoviti ticket hyperlinks when value is 'Innoviti'
 *   onClose        {function} - Called when the close (x) button is clicked
 *   downloadConfig {object}   - { fn, url, filters, timeout } forwarded to MultiCsvReportDownload
 *   formatRequest  {function} - Optional (dtParams, postData) => string. When provided,
 *                               requests are sent as JSON with this formatter.
 */
class TicketDetailTable extends Component {
  constructor(props) {
    super(props);
    this.downloadRef = React.createRef();
    this.state = {
      noData: false,
      showHistoryModal: false,
      historyData: [],
      historyTicket: null
    };
    this.openHistoryModal = this.openHistoryModal.bind(this);
    this.closeHistoryModal = this.closeHistoryModal.bind(this);
  }

  openHistoryModal(history, ticketNumber) {
    this.setState({
      showHistoryModal: true,
      historyData: Array.isArray(history) ? history : [],
      historyTicket: ticketNumber
    });
  }

  closeHistoryModal() {
    this.setState({ showHistoryModal: false, historyData: [], historyTicket: null });
  }

  componentDidMount() {
    this.detectColumnsAndInit();
  }

  componentWillUnmount() {
    const { tableId } = this.props;
    if ($.fn.DataTable.isDataTable(`#${tableId}`)) {
      $(`#${tableId}`).DataTable().clear().destroy();
    }
  }

  detectColumnsAndInit() {
    const { apiUrl, postData, formatRequest } = this.props;
    isApiResponse(false);

    const dtParams = {
      start: 0, length: 1, draw: 1,
      search: { value: '', regex: false },
      order: [{ column: 0, dir: 'desc' }]
    };

    const ajaxConfig = {
      url: config.get('API_URL') + apiUrl,
      type: 'POST',
      beforeSend: function (request) {
        request.setRequestHeader('Authorization', localStorage.usertoken);
      },
      success: (json) => {
        const rows = (json && json.data) ? json.data : [];
        if (rows.length === 0) {
          isApiResponse(true);
          this.setState({ noData: true });
          return;
        }
        const columnKeys = Object.keys(rows[0]);
        this.initDataTable(columnKeys);
      },
      error: (err) => {
        isApiResponse(true);
        alert('Failed to load details. Please try again');
        console.log('error', err);
      }
    };

    if (formatRequest) {
      ajaxConfig.contentType = 'application/json';
      ajaxConfig.data = formatRequest(dtParams, postData);
    } else {
      ajaxConfig.data = { ...postData, ...dtParams };
    }

    $.ajax(ajaxConfig);
  }

  initDataTable(columnKeys) {
    const { apiUrl, postData, tableId, objectType, formatRequest } = this.props;
    const self = this;

    if ($.fn.DataTable.isDataTable(`#${tableId}`)) {
      $(`#${tableId}`).DataTable().clear().destroy();
    }

    const visibleKeys = columnKeys.filter(k =>
      k !== 'Assignment History' && (objectType === 'Innoviti' || k !== 'Rating')
    );

    const ticketIdx = visibleKeys.indexOf('Ticket Number');
    const jobsheetIdx = visibleKeys.indexOf('Job Sheet');
    const ahCountIdx = visibleKeys.indexOf('Assignment History Count');

    const columns = visibleKeys.map(key => ({
      data: key,
      title: key
    }));

    const columnDefs = [];

    if (ahCountIdx >= 0) {
      columnDefs.push({
        targets: ahCountIdx,
        orderable: true,
        searchable: false,
        createdCell: function (cell, cellData, rowData) {
          const count = parseInt(cellData, 10);
          if (!isNaN(count) && count > 0) {
            $(cell).find('a.assignment-history-link').off('click').on('click', function (e) {
              e.preventDefault();
              self.openHistoryModal(rowData['Assignment History'], rowData['Ticket Number']);
            });
          }
        },
        render: (data) => {
          const count = parseInt(data, 10);
          if (!isNaN(count) && count > 0) {
            return `<div class='text-wrap'>
              <a href="javascript:void(0)" class="assignment-history-link"
                 style="color:#7b1fa2; text-decoration:underline; cursor:pointer;">
                ${count}
              </a>
            </div>`;
          }
          return `<div class='text-wrap'>${data == null ? 0 : data}</div>`;
        }
      });
    }

    if (ticketIdx >= 0) {
      columnDefs.push({
        targets: ticketIdx,
        render: (data) => {
          const val = data == null ? '' : String(data);
          if (objectType === 'Innoviti') {
            return `<div class='text-wrap'>
              <a href="https://innovitiservice.freshdesk.com/a/tickets/${val}"
                 target="_blank"
                 rel="noopener noreferrer"
                 style="color:#7b1fa2; text-decoration:underline;">
                ${val}
              </a>
            </div>`;
          }
          return `<div class='text-wrap'>${val}</div>`;
        }
      });
    }

    if (jobsheetIdx >= 0) {
      columnDefs.push({
        targets: jobsheetIdx,
        orderable: false,
        searchable: false,
        render: (data) => {
          const val = data == null ? '' : String(data).trim();
          if (!val) {
            return `<div class='text-wrap'>-</div>`;
          }
          return `<div class='text-wrap'>
            <a href="${val}"
               target="_blank"
               rel="noopener noreferrer"
               title="View Jobsheet"
               style="display:inline-flex; align-items:center; gap:4px; padding:4px 10px; background:#7b1fa2; color:#fff; border-radius:4px; text-decoration:none;">
              <i class="fa fa-external-link"></i> View
            </a>
          </div>`;
        }
      });
    }

    columnDefs.push({
      targets: '_all',
      render: function (data) {
        const val = data == null ? '' : String(data);
        return `<div class='text-wrap'>${val}</div>`;
      }
    });

    const ajaxConfig = {
      url: config.get('API_URL') + apiUrl,
      type: 'POST',
      dataSrc: function (json) {
        isApiResponse(true);
        return json.data || [];
      },
      beforeSend: function (request) {
        request.setRequestHeader('Authorization', localStorage.usertoken);
      },
      error: function (data) {
        isApiResponse(true);
        alert('Failed to load details. Please try again');
        console.log('error', data);
      },
      complete: function (data) {
        isApiResponse(true);
        console.log('complete', data);
      }
    };

    if (formatRequest) {
      ajaxConfig.contentType = 'application/json';
      ajaxConfig.data = function (d) {
        return formatRequest(d, postData);
      };
    } else {
      ajaxConfig.data = postData;
    }

    $(`#${tableId}`).DataTable({
      order: [[0, 'desc']],
      processing: true,
      serverSide: true,
      bDestroy: true,
      paging: true,
      pageLength: 5,
      bInfo: true,
      sPaginationType: 'simple_numbers',
      autoWidth: true,
      responsive: false,
      scrollX: true,
      searching: true,
      lengthMenu: [[5, 25, 50, 100, 500], [5, 25, 50, 100, 500]],
      ajax: ajaxConfig,
      columns: columns,
      columnDefs: columnDefs,
      headerCallback: function (thead) {
        $(thead).find('th').addClass('merchant-header-default');
      }
    });
  }

  handleDownload() {
    const { downloadConfig } = this.props;
    if (!downloadConfig || !this.downloadRef.current) return;
    const { fn, url, filters, timeout } = downloadConfig;
    this.downloadRef.current.downLoadReport(filters, url, fn, timeout || 180);
  }

  renderHistoryModal() {
    const { showHistoryModal, historyData, historyTicket } = this.state;
    if (!showHistoryModal) return null;

    const overlayStyle = {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    };
    const dialogStyle = {
      background: '#fff', borderRadius: '6px', width: '90%', maxWidth: '900px',
      maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
    };
    const headerStyle = {
      padding: '12px 20px', borderBottom: '1px solid #eee',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    };
    const bodyStyle = { padding: '16px 20px', overflowY: 'auto' };
    const closeBtnStyle = {
      background: 'transparent', border: 'none', fontSize: '20px',
      cursor: 'pointer', color: '#555'
    };

    return (
      <div style={overlayStyle} onClick={this.closeHistoryModal}>
        <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h4 style={{ margin: 0 }}>
              Assignment History
              {historyTicket ? ` - Ticket ${historyTicket}` : ''}
            </h4>
            <button type="button" style={closeBtnStyle} onClick={this.closeHistoryModal} aria-label="Close">
              <i className="fa fa-close"></i>
            </button>
          </div>
          <div style={bodyStyle}>
            {historyData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#555' }}>
                No assignment history available
              </div>
            ) : (
              <table className="table table-striped table-hover">
                <thead>
                  <tr className="merchant-header-default">
                    <th>Id</th>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Reason</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.aso_id != null ? row.aso_id : '-'}</td>
                      <td>{row.aso_name != null ? row.aso_name : '-'}</td>
                      <td>{row.assigned_at != null ? row.assigned_at : '-'}</td>
                      <td>{row.exit_reason != null ? row.exit_reason : '-'}</td>
                      <td>{row.visit_status != null ? row.visit_status : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { title, columnType, onClose, tableId } = this.props;
    const { noData } = this.state;
    return (
      <div className="ticket-details-section" style={{ marginTop: '10px' }}>
        <div className="col-md-9 no-padd">
          <div className="details-header">
            <h3>
              {title}
              {columnType && ` (${formatLabel(columnType)})`}
            </h3>
            <hr />
          </div>
        </div>
        <div className="col-md-3 pull-right">
          <div className="r-p-menu">
            <div className="report-header-icons">
              <div
                className="icon-button download-icon"
                title="Download Report"
                onClick={() => this.handleDownload()}
              >
                <i className="fa fa-download"></i>
              </div>
              <div
                className="icon-button print-icon"
                title="Close Report"
                onClick={onClose}
              >
                <i className="fa fa-close"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="clearfix"></div>
        {noData ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#555' }}>
            No data available
          </div>
        ) : (
          <div className="content-tdetails-table table-full-width table-responsive">
            <table id={tableId} className="table table-striped table-hover">
              <thead></thead>
              <tbody />
            </table>
          </div>
        )}
        <MultiCsvReportDownload ref={this.downloadRef} />
        {this.renderHistoryModal()}
      </div>
    );
  }
}

export default TicketDetailTable;
